import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Globe, Lock, Images } from "lucide-react";

/**
 * Collections — the user's own collections grid. Each card shows the cover
 * photo (or a dignified empty frame if none set), name, item count, a small
 * strip of up to 4 gallery thumbnails, and the visibility toggle (globe /
 * lock). New collections default to PRIVATE — only you can see them until
 * you flip to public.
 */
export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/collections");
      setCollections(r.data || []);
    } catch (e) {
      console.error("[collections] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/collections", { name, description });
      setName(""); setDescription("");
      toast.success("collection started — it's private until you share it");
      load();
    } catch (err) {
      console.error("[collections] create failed:", err);
      toast.error("could not create collection");
    }
  }

  async function remove(id) {
    if (!window.confirm("delete collection?")) return;
    try {
      await api.delete(`/collections/${id}`);
      load();
    } catch (e) {
      console.error("[collections] delete failed:", e);
      toast.error("could not delete");
    }
  }

  async function toggleVisibility(c) {
    if (togglingId) return;
    setTogglingId(c.id);
    const next = c.visibility === "public" ? "private" : "public";
    try {
      const r = await api.patch(`/collections/${c.id}`, { visibility: next });
      setCollections(cur => cur.map(x => x.id === c.id ? { ...x, ...r.data } : x));
      toast.success(next === "public" ? "now visible on your profile" : "now private — only you can see this");
    } catch (e) {
      console.error("[collections] visibility toggle failed:", e);
      toast.error("could not update visibility");
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="space-y-12 max-w-4xl mx-auto" data-testid="collections-page">
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
        <span className="stamp">Collections</span>
        <h1 className="mt-4 font-serif-lit text-5xl tracking-tight text-[var(--ink)] leading-tight">
          Group things <span className="italic">your way</span>.
        </h1>
        <p className="mt-3 text-[var(--ink-dusty)] max-w-xl">
          Your collections start private. Flip the lock to the globe when you want to show one on your profile.
        </p>
      </motion.div>

      <form onSubmit={create} className="placed-card p-6 space-y-5" data-testid="new-collection-form">
        <div>
          <label className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)]">Name</label>
          <input data-testid="collection-name-input" className="lc-input mt-2" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Late-night listening" />
        </div>
        <div>
          <label className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)]">Description (optional)</label>
          <input data-testid="collection-desc-input" className="lc-input mt-2" value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <button className="lc-btn" data-testid="create-collection-btn">Start the collection</button>
      </form>

      {loading ? (
        <p className="font-mono-lc text-xs tracking-[0.22em] uppercase text-[var(--ink-dusty)]" data-testid="collections-loading">Loading…</p>
      ) : collections.length === 0 ? (
        <div className="text-center py-10" data-testid="collections-empty">
          <p className="font-serif-lit italic text-2xl text-[var(--ink)]">no collections yet</p>
          <p className="mt-2 font-garamond italic text-[var(--ink-dusty)]">start one above — it&apos;s private until you say otherwise.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="collections-list">
          {collections.map(c => (
            <CollectionCard
              key={c.id}
              collection={c}
              onToggleVisibility={() => toggleVisibility(c)}
              onRemove={() => remove(c.id)}
              toggling={togglingId === c.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CollectionCard({ collection: c, onToggleVisibility, onRemove, toggling }) {
  const isPublic = c.visibility === "public";
  const strip = (c.gallery_photos || []).slice(0, 4);

  return (
    <li className="placed-card overflow-hidden flex flex-col group" data-testid={`collection-${c.id}`}>
      {/* Cover */}
      <Link
        to={`/collection/${c.id}`}
        data-testid={`open-collection-${c.id}`}
        className="block relative aspect-[16/10] bg-[var(--paper-aged)]"
      >
        {c.cover_image ? (
          <img src={c.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--ink-light)]">
            <div className="text-center">
              <Images size={28} strokeWidth={1.2} className="mx-auto opacity-50" />
              <p className="mt-2 font-mono-lc text-[9px] tracking-[0.22em] uppercase opacity-60">
                add photos
              </p>
            </div>
          </div>
        )}
        {/* Visibility pill — hovers over the cover */}
        <span
          data-testid={`collection-visibility-pill-${c.id}`}
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--card-bg)]/92 backdrop-blur-sm border border-[rgba(42,30,20,0.16)] font-cinzel text-[9px] tracking-[0.18em] uppercase text-[var(--ink)]"
        >
          {isPublic ? (
            <><Globe size={10} strokeWidth={1.8} /> public</>
          ) : (
            <><Lock size={10} strokeWidth={1.8} /> private</>
          )}
        </span>
      </Link>

      {/* Meta */}
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div>
          <Link
            to={`/collection/${c.id}`}
            className="font-serif-lit text-2xl text-[var(--ink)] tracking-tight hover:text-[var(--bronze)] transition-colors leading-tight"
          >
            {c.name}
          </Link>
          {c.description && (
            <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)] line-clamp-2">{c.description}</p>
          )}
        </div>

        {/* Photo strip (up to 4 small thumbs) */}
        {strip.length > 0 && (
          <div className="flex gap-1" data-testid={`collection-strip-${c.id}`}>
            {strip.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="w-14 h-14 object-cover rounded-[2px] border border-[rgba(42,30,20,0.08)]"
              />
            ))}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-3 border-t border-[rgba(42,30,20,0.08)]">
          <span className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
            {c.item_count || 0} item{c.item_count === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onToggleVisibility}
              disabled={toggling}
              data-testid={`collection-visibility-toggle-${c.id}`}
              className="inline-flex items-center gap-1.5 font-cinzel text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)] hover:text-[var(--bronze)] transition-colors"
              aria-label={isPublic ? "make private" : "make public"}
              title={isPublic ? "make private" : "make public"}
            >
              {isPublic ? <Lock size={11} strokeWidth={1.6} /> : <Globe size={11} strokeWidth={1.6} />}
              {toggling ? "…" : isPublic ? "make private" : "make public"}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--terracotta)]"
              data-testid={`delete-collection-${c.id}`}
            >
              remove
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}
